import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './Login.css';
import coinLogo from '../assets/COINLogo.png';
import { getStripe } from '../utils/stripe';
import CreateAccountModal from './CreateAccountModal';

// OAuth resumes are opaque, short-lived handles. Never navigate to a caller-
// supplied host or arbitrary path after accepting a password.
export const safeLoginDestination = (search, from) => {
  const params = new URLSearchParams(search);
  const next = params.get('next');
  if (next && /^\/oauth\/resume\/[A-Za-z0-9_-]{20,128}$/.test(next)) return next;
  const redirect = params.get('redirect');
  if (redirect === '/search') return redirect;
  const path = from?.pathname;
  if (['/dashboard', '/subscribe', '/faq', '/settings/ai'].includes(path)) return path;
  return '/dashboard';
};

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUrl = new URLSearchParams(location.search).get('redirect');
  const cameFromSearch = redirectUrl === '/search';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // Normalize user input so login checks are case-insensitive.
      const normalizedEmail = email.trim().toLowerCase();
      const success = await login(normalizedEmail, password);
      if (success) {
        // Check if there's a redirect URL in the query parameters
        // Navigate to the redirect URL if it exists, otherwise to dashboard
        const destination = safeLoginDestination(location.search, location.state?.from);
        if (destination.startsWith('/oauth/resume/')) window.location.assign(destination);
        else navigate(destination, { replace: true });
      } else {
        setError('Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login');
    }
  };

  const handleSubscribe = async () => {
    try {
      const response = await fetch('/api/payment/create-checkout-session', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.sessionId) {
        const stripe = await getStripe();
        await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setError('Failed to initiate subscription process. Please try again.');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const email = prompt("Please enter your email address to manage your subscription:");
      if (!email) return;
      const normalizedEmail = email.trim().toLowerCase();

      const response = await fetch('/api/payment/create-portal-session', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Could not access subscription management. Please try again.');
      }
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      setError('Error accessing subscription management. Please try again.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo-container">
          <img src={coinLogo} alt="COIN Logo" />
        </div>
        <h2>Login</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="login-button">Login</button>
        </form>
        <div className="auth-links">
          {cameFromSearch ? (
            <button type="button" className="link-button" onClick={() => setAccountModalOpen(true)}>
              Need an account? Sign Up
            </button>
          ) : (
            <Link to="/register">Need an account? Sign Up</Link>
          )}
          <Link to="/reset-password">Forgot your password?</Link>
        </div>
      </div>
      <CreateAccountModal
        open={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
        onCreated={() => navigate('/search')}
      />
    </div>
  );
}

export default Login; 
